import { useState, useCallback } from "react";

// PATCH 36 Me11 — persisted per-user widget ordering (localStorage, per browser). Items must
// carry a stable `key`. Returns the ordered items plus move/reset helpers for a drag-free UI.
export function useWidgetOrder(storageKey, items) {
  const read = () => { try { return JSON.parse(localStorage.getItem(storageKey) || "null"); } catch { return null; } };
  const [order, setOrder] = useState(read);

  const persist = useCallback((next) => {
    setOrder(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
  }, [storageKey]);

  const keys = items.map((i) => i.key);
  const ordered = (() => {
    if (!order) return items;
    const known = order.filter((k) => keys.includes(k));
    const missing = keys.filter((k) => !known.includes(k));
    const finalOrder = [...known, ...missing];
    return finalOrder.map((k) => items.find((i) => i.key === k)).filter(Boolean);
  })();

  const move = useCallback((key, dir) => {
    const cur = (order && order.length ? order.filter((k) => keys.includes(k)) : keys).slice();
    const missing = keys.filter((k) => !cur.includes(k));
    const list = [...cur, ...missing];
    const idx = list.indexOf(key);
    const to = idx + dir;
    if (idx < 0 || to < 0 || to >= list.length) return;
    [list[idx], list[to]] = [list[to], list[idx]];
    persist(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, keys, persist]);

  const reset = useCallback(() => { setOrder(null); try { localStorage.removeItem(storageKey); } catch { /* ignore */ } }, [storageKey]);

  return { ordered, move, reset, customised: !!order };
}
